import React, { useEffect, useState } from "react";
import {
  UserCircleIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  IdentificationIcon,
  CalendarIcon,
  ShieldCheckIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import {
  getAccountInfo,
  updateAccountPatch,
} from "../../services/AccountService/AccountService";
import toast from "react-hot-toast";
import {
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
} from "@heroicons/react/24/solid";
import { changePassword } from "../../services/PasswordService/PasswordService";

const ProfilePage = () => {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  // Password change states
  const [isPasswordFormOpen, setIsPasswordFormOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  useEffect(() => {
    const loadAccountInfo = async () => {
      try {
        setLoading(true);
        const info = await getAccountInfo();
        setAccount(info);
      } catch (err) {
        console.error(err);
        toast.error("Профиль мәліметтерін жүктеу сәтсіз аяқталды.");
      } finally {
        setLoading(false);
      }
    };
    loadAccountInfo();
  }, []);

  const getFullName = () => {
    if (!account) return "";
    const fullName =
      account.last_name || account.first_name
        ? `${account.last_name || ""} ${account.first_name || ""}`.trim()
        : null;
    return fullName || "Аты-жөні көрсетілмеген";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("kk-KZ", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Edit handler
  const handleEditProfile = () => {
    setEditFormData({
      firstName: account?.first_name || "",
      lastName: account?.last_name || "",
      email: account?.email || "",
    });
    setIsEditFormOpen(true);
  };

  // Update profile handler
  const handleUpdateProfile = async () => {
    if (!account) return;

    // Basic validation
    if (!editFormData.firstName.trim()) {
      toast.error("Атын енгізіңіз");
      return;
    }
    if (!editFormData.lastName.trim()) {
      toast.error("Тегін енгізіңіз");
      return;
    }
    if (!editFormData.email.trim()) {
      toast.error("Email енгізіңіз");
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editFormData.email)) {
      toast.error("Дұрыс email форматын енгізіңіз");
      return;
    }

    try {
      setIsUpdating(true);

      // Update profile
      const updatedAccount = await updateAccountPatch(account.uuid, {
        first_name: editFormData.firstName,
        last_name: editFormData.lastName,
        email: editFormData.email,
      });

      // Update local state with new data
      setAccount(updatedAccount);
      toast.success("Профиль сәтті жаңартылды!");

      // Reset form and close modal
      setEditFormData({ firstName: "", lastName: "", email: "" });
      setIsEditFormOpen(false);
    } catch (error) {
      console.error("Update profile error:", error);
      toast.error("Профильді жаңарту кезінде қате пайда болды");
    } finally {
      setIsUpdating(false);
    }
  };

  // Password change handlers
  const handleChangePassword = () => {
    setPasswordFormData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setIsPasswordFormOpen(true);
  };

  const handleUpdatePassword = async () => {
    // Basic validation
    if (!passwordFormData.currentPassword.trim()) {
      toast.error("Қазіргі құпиясөзді енгізіңіз");
      return;
    }
    if (!passwordFormData.newPassword.trim()) {
      toast.error("Жаңа құпиясөзді енгізіңіз");
      return;
    }
    if (passwordFormData.newPassword.length < 6) {
      toast.error("Жаңа құпиясөз кемінде 6 таңбадан тұруы керек");
      return;
    }
    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      toast.error("Жаңа құпиясөзді растау сәйкес келмейді");
      return;
    }

    try {
      setIsChangingPassword(true);

      await changePassword(
        passwordFormData.currentPassword,
        passwordFormData.newPassword
      );

      toast.success("Құпиясөз сәтті өзгертілді!");

      // Reset form and close modal
      setPasswordFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setIsPasswordFormOpen(false);
    } catch (error) {
      console.error("Change password error:", error);
      toast.error("Құпиясөзді өзгерту кезінде қате пайда болды");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="flex items-center space-x-6">
                <div className="w-24 h-24 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-3">
                  <div className="h-8 bg-gray-200 rounded-lg w-1/3"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className=" p-6">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header Card */}
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-[#674DA2] to-[#5a4291] p-8 text-white">
            <div className="flex items-center space-x-6">
              <div className="w-24 h-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <UserCircleIcon className="w-16 h-16" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-2">{getFullName()}</h1>
                <p className="text-white text-opacity-90 text-lg">
                  {account?.email || "Email көрсетілмеген"}
                </p>
                <div className="flex items-center mt-2 text-white text-opacity-80">
                  <BuildingOfficeIcon className="w-4 h-4 mr-2" />
                  <span>
                    {account?.organization?.name || "Ұйым көрсетілмеген"}
                  </span>
                </div>
              </div>
              <button
                onClick={handleEditProfile}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-xl p-3 transition-colors"
              >
                <PencilSquareIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Account Information */}
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-[#674DA2] bg-opacity-10 rounded-xl flex items-center justify-center">
                <IdentificationIcon className="w-6 h-6 text-[#674DA2]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 ml-3">
                Жеке мәліметтер
              </h2>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <UserCircleIcon className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Аты</p>
                  <p className="text-gray-900 font-medium">
                    {account?.first_name || "—"}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <UserCircleIcon className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Тегі</p>
                  <p className="text-gray-900 font-medium">
                    {account?.last_name || "—"}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <EnvelopeIcon className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">
                    Email
                  </p>
                  <p className="text-gray-900 font-medium break-all">
                    {account?.email || "—"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Organization & Roles */}
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-[#674DA2] bg-opacity-10 rounded-xl flex items-center justify-center">
                <BuildingOfficeIcon className="w-6 h-6 text-[#674DA2]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 ml-3">
                Ұйым және рөлдер
              </h2>
            </div>

            <div className="space-y-6">
              <div className="p-4 bg-gradient-to-r from-[#674DA2] to-[#906fde] bg-opacity-5 rounded-xl border border-[#674DA2]">
                <div className="flex items-center space-x-3">
                  <BuildingOfficeIcon className="w-6 h-6 text-white" />
                  <div>
                    <p className="text-sm font-medium text-white mb-1">Ұйым</p>
                    <p className="text-white font-semibold text-lg">
                      {account?.organization?.name || "Ұйым көрсетілмеген"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <ShieldCheckIcon className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-2">
                    Рөлдер
                  </p>
                  {account?.roles?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {account.roles.map((role, index) => (
                        <span
                          key={role.uuid || role.id || index}
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-[#674DA2] bg-opacity-10 text-[#674DA2] border border-[#674DA2] border-opacity-20"
                        >
                          {role.name || "—"}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">
                      Рөлдер көрсетілмеген
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">
                    Статус
                  </p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {account?.status === "active"
                      ? "Белсенді"
                      : account?.status || "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="bg-white rounded-3xl p-8 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-[#674DA2] bg-opacity-10 rounded-xl flex items-center justify-center">
                <LockClosedIcon className="w-6 h-6 text-[#674DA2]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 ml-3">
                Қауіпсіздік
              </h2>
            </div>
            <button
              onClick={handleChangePassword}
              className="bg-[#674DA2] hover:bg-[#5a4291] text-white px-6 py-2 rounded-xl font-medium transition-colors duration-200"
            >
              Құпиясөзді өзгерту
            </button>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
              <LockClosedIcon className="w-5 h-5 text-gray-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500 mb-1">Құпиясөз</p>
              <p className="text-gray-900 font-medium">••••••••</p>
            </div>
          </div>
        </div>

        {/* Additional Info Card */}
        {account?.middle_name && (
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Қосымша мәліметтер
            </h3>
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <UserCircleIcon className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Әкесінің аты
                </p>
                <p className="text-gray-900 font-medium">
                  {account.middle_name}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Profile Modal */}
      {isEditFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl transform transition-all">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Профильді өңдеу
            </h2>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Аты
                </label>
                <input
                  type="text"
                  value={editFormData.firstName}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      firstName: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                  placeholder="Атын енгізіңіз"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Тегі
                </label>
                <input
                  type="text"
                  value={editFormData.lastName}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      lastName: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                  placeholder="Тегін енгізіңіз"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, email: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                  placeholder="email@example.com"
                />
              </div>
              <div className="flex space-x-4 mt-4">
                <button
                  onClick={handleUpdateProfile}
                  disabled={isUpdating}
                  className={`${
                    isUpdating
                      ? "opacity-70 cursor-not-allowed"
                      : "hover:from-[#5a4291] hover:to-[#4d3e7c] transform hover:scale-105"
                  } bg-gradient-to-r from-[#674DA2] to-[#5a4291] text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex-1`}
                >
                  {isUpdating ? "Жаңартылуда..." : "Өзгерістерді сақтау"}
                </button>
                <button
                  onClick={() => {
                    if (isUpdating) return;
                    setIsEditFormOpen(false);
                    setEditFormData({
                      firstName: "",
                      lastName: "",
                      email: "",
                    });
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex-1"
                >
                  Бас тарту
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {isPasswordFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl transform transition-all">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Құпиясөзді өзгерту
            </h2>
            <div className="grid grid-cols-1 gap-6">
              {[
                {
                  key: "currentPassword",
                  toggle: "current",
                  label: "Қазіргі құпиясөз",
                  placeholder: "Қазіргі құпиясөзіңізді енгізіңіз",
                },
                {
                  key: "newPassword",
                  toggle: "new",
                  label: "Жаңа құпиясөз",
                  placeholder: "Жаңа құпиясөзіңізді енгізіңіз",
                },
                {
                  key: "confirmPassword",
                  toggle: "confirm",
                  label: "Жаңа құпиясөзді растау",
                  placeholder: "Жаңа құпиясөзіңізді қайталаңыз",
                },
              ].map(({ key, toggle, label, placeholder }) => (
                <div key={key}>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    {label}
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords[toggle] ? "text" : "password"}
                      value={passwordFormData[key]}
                      onChange={(e) =>
                        setPasswordFormData({
                          ...passwordFormData,
                          [key]: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                      placeholder={placeholder}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility(toggle)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showPasswords[toggle] ? (
                        <EyeSlashIcon className="w-5 h-5" />
                      ) : (
                        <EyeIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              ))}

              {/* Password Requirements */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  Құпиясөз талаптары:
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li className="flex items-center">
                    <div className="w-1 h-1 bg-blue-600 rounded-full mr-2"></div>
                    Кемінде 6 таңба болуы керек
                  </li>
                  <li className="flex items-center">
                    <div className="w-1 h-1 bg-blue-600 rounded-full mr-2"></div>
                    Қауіпсіз құпиясөз қолданыңыз
                  </li>
                </ul>
              </div>

              <div className="flex space-x-4 mt-4">
                <button
                  onClick={handleUpdatePassword}
                  disabled={isChangingPassword}
                  className={`${
                    isChangingPassword
                      ? "opacity-70 cursor-not-allowed"
                      : "hover:from-[#5a4291] hover:to-[#4d3e7c] transform hover:scale-105"
                  } bg-gradient-to-r from-[#674DA2] to-[#5a4291] text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex-1`}
                >
                  {isChangingPassword ? "Өзгертілуде..." : "Құпиясөзді өзгерту"}
                </button>
                <button
                  onClick={() => {
                    if (isChangingPassword) return;
                    setIsPasswordFormOpen(false);
                    setPasswordFormData({
                      currentPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                    setShowPasswords({
                      current: false,
                      new: false,
                      confirm: false,
                    });
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex-1"
                >
                  Бас тарту
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
